/**
 * ContextAuditScreen — Auditoria de Contexto (área administrativa).
 *
 * A pergunta desta tela não é "o que existe no banco". É:
 *   "de tudo que o sistema acumulou sobre este projeto, o que chega ao modelo que
 *    escreve o código — e o que fica de fora?"
 *
 * Foi uma auditoria manual que revelou o problema: ~380 mil caracteres de escopo
 * acumulado entregavam ~8 mil ao gerador (≈2%), com metade das memórias sem vetor e o
 * repositório-alvo nunca indexado. Nada na plataforma mostrava isso. Aqui esse
 * diagnóstico vira contínuo e por projeto.
 */
import { useState } from 'react';
import { C } from '@/theme/palette';
import { LoadingSpinner, QueryError, Card, Badge } from '@/components/ui';
import {
  useContextAudit,
  useContextAuditProjects,
  useExecutionTraceReport,
} from '@/api/contextAudit';
import { AgentesPorEtapa } from '@/components/agentes/AgentesPorEtapa';

const num = (n) => Number(n || 0).toLocaleString('pt-BR');

function Barra({ pct, cor }) {
  return (
    <div style={{ height: 6, background: `${C.neutral}22`, borderRadius: 3, overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(100, Math.max(0, pct))}%`, height: '100%', background: cor }} />
    </div>
  );
}

function Alertas({ itens }) {
  if (!itens?.length) return null;
  return (
    <Card style={{ borderColor: `${C.warn}55`, marginBottom: 16 }}>
      <div style={{ fontWeight: 600, color: C.warn, marginBottom: 8 }}>
        {itens.length} ponto(s) que afetam a qualidade da geração
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, color: C.neutralDim, lineHeight: 1.7 }}>
        {itens.map((a, i) => <li key={i}>{a}</li>)}
      </ul>
    </Card>
  );
}

function TabelaFontes({ fontes }) {
  const cols = '2.4fr 0.7fr 1fr 1.2fr 2fr';
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Fontes de contexto do projeto</div>
      <div style={{ color: C.neutralMuted, fontSize: 12, marginBottom: 12 }}>
        &quot;Chega ao prompt&quot; é o que de fato o gerador enxerga. O resto existe no banco
        e não influencia a geração.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 8, fontSize: 12,
                    color: C.neutralMuted, paddingBottom: 6 }}>
        <div>Fonte</div><div>Registros</div><div>Caracteres</div>
        <div>Chega ao prompt</div><div>Observação</div>
      </div>
      {fontes.map((f, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: cols, gap: 8,
                              padding: '10px 0', borderTop: `1px solid ${C.blackBorder}`,
                              fontSize: 13, alignItems: 'center' }}>
          <div>{f.fonte}</div>
          <div>{num(f.registros)}</div>
          <div style={{ color: f.estoura_teto ? C.bad : C.text }}>
            {num(f.caracteres)}
            {f.teto ? <span style={{ color: C.neutralMuted }}> / {num(f.teto)}</span> : null}
          </div>
          <div>
            <Badge color={f.chega_ao_prompt === 'não' ? C.bad : C.ok} light>
              {f.chega_ao_prompt}
            </Badge>
            {f.estoura_teto ? (
              <span style={{ marginLeft: 6 }}><Badge color={C.bad} light>truncado</Badge></span>
            ) : null}
          </div>
          <div style={{ color: C.neutralMuted, fontSize: 12 }}>{f.observacao}</div>
        </div>
      ))}
    </Card>
  );
}

function SaudeDoIndice({ indice }) {
  if (!indice?.length) return null;
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Saúde do índice vetorial</div>
      <div style={{ color: C.neutralMuted, fontSize: 12, marginBottom: 12 }}>
        Memória sem vetor é invisível à busca — está gravada e nunca será recuperada.
      </div>
      {indice.map((m) => (
        <div key={m.tipo} style={{ padding: '10px 0', borderTop: `1px solid ${C.blackBorder}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13,
                        marginBottom: 6 }}>
            <span>{m.tipo}</span>
            <span style={{ color: m.sem_vetor ? C.bad : C.ok }}>
              {num(m.com_vetor)} de {num(m.total)} com vetor ({m.pct_com_vetor}%)
            </span>
          </div>
          <Barra pct={m.pct_com_vetor} cor={m.pct_com_vetor === 100 ? C.ok : C.bad} />
        </div>
      ))}
    </Card>
  );
}

function Repositorios({ repos }) {
  if (!repos?.length) return null;
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12 }}>Repositórios indexados</div>
      {repos.map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '2.5fr 1fr 1fr 1.2fr',
                              gap: 8, padding: '10px 0', fontSize: 13,
                              borderTop: `1px solid ${C.blackBorder}`, alignItems: 'center' }}>
          <div>{r.fullName}<span style={{ color: C.neutralMuted }}> · {r.branch}</span></div>
          <div>
            <Badge color={r.cloneStatus === 'ready' ? C.ok : C.bad} light>
              {r.cloneStatus || 'pendente'}
            </Badge>
          </div>
          <div style={{ color: C.neutralMuted }}>{r.role || '—'}</div>
          <div style={{ color: r.indexedDocs ? C.text : C.bad }}>
            {num(r.indexedDocs)} doc(s) indexado(s)
          </div>
        </div>
      ))}
    </Card>
  );
}

function Blueprint({ bp }) {
  if (!bp || !Object.keys(bp).length) return null;
  const linha = (rot, val) => (
    <div style={{ display: 'flex', gap: 8, padding: '6px 0', fontSize: 13 }}>
      <span style={{ color: C.neutralMuted, minWidth: 150 }}>{rot}</span>
      <span>{Array.isArray(val) ? (val.join(', ') || '—') : String(val ?? '—')}</span>
    </div>
  );
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Blueprint aprovado (contrato)</div>
      <div style={{ color: C.neutralMuted, fontSize: 12, marginBottom: 8 }}>
        A geração é conferida contra isto. Divergência vira violação, não sugestão.
      </div>
      {linha('Linguagens', bp.languages)}
      {linha('Frameworks', bp.frameworks)}
      {linha('Bancos de dados', bp.databases)}
      {linha('Infraestrutura', bp.infrastructure)}
      {linha('Artefatos previstos', `${(bp.selected_artifacts || []).length} item(ns)`)}
      {linha('Arquitetura-alvo',
        `${num(bp.target_architecture_chars)} ch — só ${num(bp.target_architecture_no_prompt)} entram no prompt`)}
    </Card>
  );
}

const ROTULO_ORIGEM = {
  licao: 'lição do projeto',
  episodio: 'episódio anterior',
  licao_tenant: 'de outro projeto — referência',
};

const ROTULO_OMITIDO = { licoes: 'lições', episodios: 'episódios', licoes_tenant: 'lições de outros projetos' };

/**
 * MemoriaPorEtapa — o bloco [3] ("O que você já aprendeu neste projeto") de cada agente,
 * por etapa (SPEC A6 FR-11, NFR-4): quantos e quais itens entraram, o texto de cada um
 * (lido sob a RLS do usuário), o que ficou fora do limite e se houve corte por tamanho.
 */
export function MemoriaPorEtapa({ memoria }) {
  if (!Array.isArray(memoria) || memoria.length === 0) return null;
  return (
    <div data-testid="memoria-por-etapa" style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Memória do agente no prompt</div>
      <div style={{ color: C.neutralMuted, fontSize: 12, marginBottom: 8 }}>
        O que cada agente recebeu como “O que você já aprendeu neste projeto”, por etapa.
      </div>
      {memoria.map((m, idx) => {
        const itens = Array.isArray(m.itens) ? m.itens : [];
        const total = (m.licoes?.length || 0) + (m.episodios?.length || 0) + (m.licoes_tenant?.length || 0);
        const omit = Object.entries(m.omitidos || {}).filter(([, v]) => Number(v) > 0);
        return (
          <div key={`${m.etapa}-${m.agente}-${idx}`} data-testid={`memoria-${m.etapa}-${m.agente}`}
            style={{ border: `1px solid ${C.blackBorder}`, borderRadius: 6, padding: 10, marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
              <strong>{m.etapa || '—'}</strong>
              <code style={{ color: C.neutralDim }}>{m.agente}</code>
              <span style={{ color: C.neutralMuted, fontSize: 12 }}>
                {num(m.licoes?.length)} lição(ões) · {num(m.episodios?.length)} episódio(s) ·{' '}
                {num(m.licoes_tenant?.length)} de outros projetos · {num(m.chars)} caracteres
              </span>
              {m.cortado ? <Badge color={C.warn}>cortado pelo teto</Badge> : null}
            </div>
            {total === 0 ? (
              <div style={{ color: C.neutralMuted, fontSize: 12, marginTop: 6 }}>
                Nenhum item de memória entrou (o agente ainda não tem memória neste projeto).
              </div>
            ) : null}
            {omit.length ? (
              <div style={{ color: C.warn, fontSize: 12, marginTop: 6 }}>
                Fora do limite: {omit.map(([k, v]) => `${num(v)} ${ROTULO_OMITIDO[k] || k}`).join(' · ')}
              </div>
            ) : null}
            {itens.length ? (
              <ul style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.5, fontSize: 12 }}>
                {itens.map((it) => (
                  <li key={`${it.origem}-${it.id}`} data-testid={`memoria-item-${it.id}`}>
                    <span style={{ color: C.neutralMuted }}>[{ROTULO_ORIGEM[it.origem] || it.origem}]</span>{' '}
                    {it.disponivel
                      ? <span style={{ color: C.neutral }}>{it.conteudo}</span>
                      : <span style={{ color: C.neutralMuted }}>texto indisponível (sem permissão ou item removido) — <code>{it.id}</code></span>}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function RastroDaExecucao() {
  const [id, setId] = useState('');
  const [busca, setBusca] = useState('');
  const { data, isLoading, isError, error } = useExecutionTraceReport(busca);

  return (
    <Card>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Rastro de uma execução</div>
      <div style={{ color: C.neutralMuted, fontSize: 12, marginBottom: 12 }}>
        Relatório estruturado do que os agentes fizeram: por que o motor parou, quanto de
        contexto foi truncado, quais arquivos foram descartados e por quê.
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); setBusca(id.trim()); }}
        style={{ display: 'flex', gap: 8, marginBottom: 12 }}
      >
        <input
          value={id}
          onChange={(e) => setId(e.target.value)}
          placeholder="ID da execução (UUID)"
          aria-label="ID da execução"
          style={{ flex: 1, padding: '8px 10px', background: C.blackElev2,
                   border: `1px solid ${C.blackBorder}`, borderRadius: 6, color: C.text }}
        />
        <button
          type="submit"
          style={{ padding: '8px 16px', background: 'transparent', color: C.accent,
                   border: `1px solid ${C.accent}`, borderRadius: 6, cursor: 'pointer' }}
        >
          Ver rastro
        </button>
      </form>
      {isLoading && busca ? <LoadingSpinner /> : null}
      {isError ? <QueryError error={error} /> : null}
      {data && !data.disponivel ? (
        <div style={{ color: C.neutralMuted, fontSize: 13 }}>{data.relatorio}</div>
      ) : null}
      {data?.disponivel ? (
        <pre style={{ margin: 0, padding: 12, background: C.blackElev2, borderRadius: 6,
                      fontSize: 12, lineHeight: 1.5, overflowX: 'auto',
                      maxHeight: 520, overflowY: 'auto' }}>
          {data.relatorio || JSON.stringify(data.eventos, null, 2)}
        </pre>
      ) : null}
      {data ? <AgentesPorEtapa agentes={data.agentes} /> : null}
      {data ? <MemoriaPorEtapa memoria={data.memoria} /> : null}
    </Card>
  );
}

export function ContextAuditScreen() {
  const [sel, setSel] = useState('');
  const projetos = useContextAuditProjects();
  const auditoria = useContextAudit(sel);

  if (projetos.isLoading) return <LoadingSpinner />;
  if (projetos.isError) {
    return <QueryError error={projetos.error} onRetry={projetos.refetch} />;
  }

  const items = projetos.data?.items || [];
  const a = auditoria.data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 20 }}>Auditoria de Contexto</h2>
        <div style={{ color: C.neutralMuted, fontSize: 13, marginTop: 4 }}>
          O que o gerador de código realmente tem por projeto — e o que fica de fora.
        </div>
      </div>

      <Card>
        <label htmlFor="proj" style={{ display: 'block', fontSize: 12,
                                       color: C.neutralMuted, marginBottom: 6 }}>
          Projeto
        </label>
        <select
          id="proj"
          value={sel}
          onChange={(e) => setSel(e.target.value)}
          style={{ width: '100%', padding: '8px 10px', background: C.blackElev2,
                   border: `1px solid ${C.blackBorder}`, borderRadius: 6, color: C.text }}
        >
          <option value="">Selecione um projeto…</option>
          {items.map((p) => (
            <option key={p.projectId} value={p.projectId}>
              {p.name} — {num(p.caracteresAcumulados)} ch acumulados
              {p.alertas.length ? ` · ${p.alertas.length} alerta(s)` : ''}
            </option>
          ))}
        </select>
        {items.length === 0 ? (
          <div style={{ marginTop: 12, padding: 16, border: `1px dashed ${C.blackBorder}`,
                        borderRadius: 6, color: C.neutralMuted, fontSize: 13 }}>
            Nenhum projeto para auditar ainda.
          </div>
        ) : null}
      </Card>

      {sel && auditoria.isLoading ? <LoadingSpinner /> : null}
      {sel && auditoria.isError ? (
        <QueryError error={auditoria.error} onRetry={auditoria.refetch} />
      ) : null}

      {a ? (
        <>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'baseline', marginBottom: 10 }}>
              <div style={{ fontWeight: 600 }}>
                {a.name}
                <span style={{ color: C.neutralMuted, fontWeight: 400 }}> · {a.projectType}</span>
              </div>
              <div style={{ fontSize: 13 }}>
                <span style={{ color: C.neutralMuted }}>chega ao gerador: </span>
                <strong style={{ color: a.totais.pct_que_chega < 20 ? C.bad : C.ok }}>
                  {a.totais.pct_que_chega}%
                </strong>
              </div>
            </div>
            <Barra pct={a.totais.pct_que_chega}
                   cor={a.totais.pct_que_chega < 20 ? C.bad : C.ok} />
            <div style={{ color: C.neutralMuted, fontSize: 12, marginTop: 8 }}>
              {num(a.totais.caracteres_que_chegam)} de {num(a.totais.caracteres_acumulados)}{' '}
              caracteres acumulados sobre o projeto.
            </div>
          </Card>

          <Alertas itens={a.alertas} />
          <Blueprint bp={a.blueprint} />
          <TabelaFontes fontes={a.fontes} />
          <SaudeDoIndice indice={a.indice} />
          <Repositorios repos={a.repositorios} />
        </>
      ) : null}

      <RastroDaExecucao />
    </div>
  );
}

export default ContextAuditScreen;
